import {
  GoalPosition,
  PitchDistance,
  PitchPosition,
} from '@collector/mobile-feature-goal-and-pitch-positions'
import {
  HandballAttributeId,
  HandballGoalAttribute,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { IncidentDetailsFillState } from '@mobile/views/Relation/DetailsPanel/enums'

import { DetailConfiguration } from '../../composables/useSportOptions/types'
import { getIncidentAttributesDetailsFillState } from '../../Modals/utils'
import {
  getGoalPositionFillState,
  getPitchDistanceFillState,
  getPitchPositionFillState,
} from './components/ThrowPositionDetailsModal/utils'
import { GroupName } from './consts'

interface FillStateConfig<T extends HandballAttributeId> {
  groups?: Map<GroupName, DetailConfiguration<T>>
  pitchPositionAttributeMap?: Map<PitchPosition, HandballAttributeId>
  pitchDistanceAttributeMap?: Map<PitchDistance, HandballAttributeId>
  goalPositionAttributeMap?: Map<GoalPosition, HandballAttributeId>
  checkGoalkeeper?: boolean
  checkAssistant?: boolean
}

function hasAutoFilledAttribute(incident: ProbableIncident): boolean {
  const attributes = incident.attribute_ids as HandballAttributeId[]
  const autoFilledAttributes = new Set<HandballAttributeId>([
    HandballGoalAttribute.PenaltyGoal,
  ])
  return attributes.some((attr) => autoFilledAttributes.has(attr))
}

export function getHandballIncidentDetailsFillState<
  T extends HandballAttributeId,
>(
  incident: ProbableIncident,
  config: FillStateConfig<T>,
): { state: IncidentDetailsFillState } {
  const states: IncidentDetailsFillState[] = []

  // Add pitch position fill state if configured
  if (config.pitchPositionAttributeMap) {
    if (!hasAutoFilledAttribute(incident))
      states.push(
        getPitchPositionFillState(incident, config.pitchPositionAttributeMap),
      )
  }

  // Add pitch distance fill state if configured
  if (config.pitchDistanceAttributeMap) {
    if (!hasAutoFilledAttribute(incident))
      states.push(
        getPitchDistanceFillState(incident, config.pitchDistanceAttributeMap),
      )
  }

  // Add goal position fill state if configured
  if (config.goalPositionAttributeMap) {
    states.push(
      getGoalPositionFillState(incident, config.goalPositionAttributeMap),
    )
  }

  // Add goalkeeper fill state if configured
  if (config.checkGoalkeeper) {
    if (!incident.attribute_ids?.includes(HandballGoalAttribute.EmptyNetGoal)) {
      states.push(
        incident.additional_info?.goalkeeper_id
          ? IncidentDetailsFillState.Filled
          : IncidentDetailsFillState.NotFilled,
      )
    }
  }

  // Add assistant fill state if configured
  if (config.checkAssistant) {
    states.push(
      incident.additional_info?.assistant_id
        ? IncidentDetailsFillState.Filled
        : IncidentDetailsFillState.NotFilled,
    )
  }

  // Add attributes fill state
  if (config.groups) {
    if (!hasAutoFilledAttribute(incident))
      states.push(
        getIncidentAttributesDetailsFillState(config.groups, incident),
      )
  }

  // Determine overall state
  if (states.every((s) => s === IncidentDetailsFillState.Filled)) {
    return { state: IncidentDetailsFillState.Filled }
  }

  if (states.every((s) => s === IncidentDetailsFillState.NotFilled)) {
    return { state: IncidentDetailsFillState.NotFilled }
  }

  return { state: IncidentDetailsFillState.HalfFilled }
}
